import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { imgUrl } from '~commonInfra/utils/ImgUrl';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';

import { Messages } from '../../../codegen/Localize';
import { cssClass } from '../../../style';
import { useRadioEditContext } from '../../utils/RadioEditProvider';

const cssPrefix = 'DescriptorBlock';

export const getEditButtonClass = () => cssClass(cssPrefix, 'button', {
  marginLeft: CommonStyles.Sizes.DefaultSpace,
  minWidth: CommonStyles.Sizes.NormalIconSize,
  height: CommonStyles.Sizes.NormalIconSize,
  verticalAlign: 'middle',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
  display: 'inline-block',
  $nest: {
    '&:hover, &:focus': {
      backgroundColor: CommonStyles.Colors.Grey245,
      backgroundImage: imgUrl('edit_small_hover.svg')
    }
  }
});

const descriptorBlockTitleClass = cssClass(cssPrefix, 'title', {
  color: '#9B9B9B',
  paddingBottom: CommonStyles.Sizes.DefaultSpace,
});

const descriptorWithTitleBlockClass = cssClass(cssPrefix, 'block', {
  padding: CommonStyles.Sizes.MediumSpace,
  paddingTop: CommonStyles.Sizes.DefaultSpace,
  fontSize: '13px',
  lineHeight: '1.5em',
});

const descriptorWithoutTitleBlockClass = cssClass(cssPrefix, 'block', {
  paddingTop: CommonStyles.Sizes.DefaultSpace,
});

const getEditableDescriptorBlockClass = () => cssClass(cssPrefix, 'editableBlock', {
  $nest: {
    '&:hover': {
      backgroundColor: CommonStyles.Colors.Grey245,
    },
    [`&:hover .${getEditButtonClass}`]: {
      backgroundImage: imgUrl('edit_small_normal.svg'),
    }
  }
});

const resolveButtons = cssClass(cssPrefix, 'resolveButtons', {
  paddingTop: CommonStyles.Sizes.SmallSpace,
  overflow: 'hidden',
});

const paddedButton: React.CSSProperties = {
  marginLeft: CommonStyles.Sizes.DefaultSpace,
  float: 'right',
};

export interface DescriptorBlockChildProps<T> {
  value?: T;
  isEditing?: boolean;
  onChange?: (newState: T) => void;
}

export type DescriptorBlockChild<T> = React.FC<DescriptorBlockChildProps<T>>;

export interface DescriptorBlockProps<T> {
  readonly title: string;
  readonly content: T;
  readonly contentDisplay?: DescriptorBlockChild<T>;
  readonly onSave?: (newState: T) => Promise<boolean>;
  readonly onCancel?: () => void;
  readonly onEditChange?: (isEditing: boolean) => void;
  readonly keepEditModeOnSaveFail?: boolean;
  readonly emptyMessage?: string;
  readonly editable?: boolean;
  readonly containerStyle?: React.CSSProperties;
  readonly enableSaveButton?: (value: T) => boolean;
  readonly testId?: string;
}

export function DescriptorBlock<T>(props: DescriptorBlockProps<T>) {
  const {isReadonly, setIsEditing, isEditing} = useRadioEditContext();

  // Editing allowed only if the data is editable and the radio context allows changes
  const canEdit = !isReadonly && props.editable;

  const isEmpty = !props.content || Array.isArray(props.content) && props.content.length === 0;

  const [savedValue, onSavedValueChange] = React.useState(props.content);
  const [value, onChange] = React.useState(savedValue);
  const childBlock =
    props.contentDisplay && props.contentDisplay({value, isEditing, onChange}) ||
    typeof value === 'string' && value ||
    props.emptyMessage && <i>{props.emptyMessage}</i> ||
    null;

  React.useEffect(
    () => {
      props.onEditChange && props.onEditChange(isEditing);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEditing]
  );

  if(isEmpty && !canEdit) { return null; }

  const shouldEditOnClick = canEdit && !isEditing;
  const disableSave = _.isUndefined(props.enableSaveButton)? false : !props.enableSaveButton(value);

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <div
      style={props.containerStyle}
      className={`${props.title? descriptorWithTitleBlockClass : descriptorWithoutTitleBlockClass} ${shouldEditOnClick && getEditableDescriptorBlockClass || ''}`}
      onClick={shouldEditOnClick ? () => setIsEditing(true) : _.noop}
      data-tb-test-id={`${props.testId || props.title}-descriptor-block`}>
      { getEditableBlock() }
      {isEditing ? <div className={resolveButtons}>
        <ButtonWidget
          style={paddedButton}
          buttonType={ButtonType.Primary}
          disabled={disableSave}
          testId={`edit-${props.testId || props.title}-save`}
          handleClick={
            !props.onSave?
            () => {
              setIsEditing(false);
              onSavedValueChange(value);
            }
            :
            async () => {
              const successful = await props.onSave(value);
              if(successful) {
                setIsEditing(false);
                onSavedValueChange(value);
              } else if(!props.keepEditModeOnSaveFail) {
                setIsEditing(false);
                onChange(savedValue);
              }
            }
          }
        >
          {Messages.save()}
        </ButtonWidget>
        <ButtonWidget
          style={paddedButton}
          buttonType={ButtonType.Outline}
          testId={`edit-${props.testId || props.title}-cancel`}
          handleClick={() => {
            setIsEditing(false);
            onChange(savedValue);
            props.onCancel && props.onCancel();
          }}
          >
            {Messages.cancel()}
          </ButtonWidget>
      </div>: null}
    </div>
  );

  function getEditableBlock(): JSX.Element {
    return props.title ?
      getEditableBlockWithTitle() :
      getEditableBlockWithoutTitle();
  }

  function getEditableBlockWithTitle(): JSX.Element {
    return (<>
      <div className={descriptorBlockTitleClass}>
        <label>{props.title}</label>
        {getEditableDiv()}
      </div>
      {childBlock}
    </>);
  }

  function getEditableBlockWithoutTitle(): JSX.Element {
    return (
      <div className={SpaceHeaderStyles.RowAlignedCenter}>
        {childBlock}
        {getEditableDiv()}
      </div>);
  }

  function getEditableDiv(): JSX.Element {
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
    return (<>
      {shouldEditOnClick ?
        <div
          tabIndex={0}
          className={getEditButtonClass()}
          onClick={() => setIsEditing(true)}
          onKeyDown={e => e.key === 'Enter' ? setIsEditing(true) : _.noop()} />
        : null
      }
    </>);
  }
}
